import wretch from 'wretch';
import { call, select } from 'typed-redux-saga';
import { setIn } from 'utilitify';
import { put } from 'redux-saga/effects';

import {
  IDashboardApi,
  IDashboardResponseModel,
  ISaveFilterResponseModel,
  IUpdateFilterResponseModel,
  IDeleteFilterResponseModel,
  IExcelExportRequestModel,
  ISavedFilterModel,
  ISaveFilterRequestModel,
  IUpdateFilterRequestModel,
  IDeleteFilterRequestModel,
  IGridDataRequestOptions,
  dashboardActions,
  PageMode,
} from '@crm-framework/dashboard';
import { handleServerError } from '@crm-framework/utils';

import { layoutsActions } from '@crm-package/layouts/store';
import {
  loadAndSaveDictionariesByName,
  getDictionaryByNameSelector,
} from '@crm-package/dictionaries';
import { getLayoutState } from '@crm-package/layouts';

import { mapDashboard, mapIsDemoFilter } from './mappers';
import { dictionariesNames } from './consts';
import { ISDEMO_FIELD_NAME } from './components';
import {
  IDashboardFromServer,
  IGridDataFromServer,
  IGridTotalsResponseModel,
} from './types';
import { getExcelValue } from './utils';

async function getDashboard(url: string): Promise<IDashboardFromServer> {
  return wretch(url).get().json();
}

export function* apiGetDashboard(
  url: string,
  disableFastMode?: boolean,
  isSupportCryptoCurrencies?: boolean,
  dashboardName?: string
): Generator<any, IDashboardResponseModel, any> {
  yield call(loadAndSaveDictionariesByName, dictionariesNames);

  const dictionaries = yield* select(
    getDictionaryByNameSelector,
    dictionariesNames
  );

  if (isSupportCryptoCurrencies) {
    yield* loadAndSaveDictionariesByName([...dictionariesNames, 'currencies']);
  }

  const dashboard = yield* call(getDashboard, url);

  const { mainDashboardName } = yield select(getLayoutState);

  if (
    (mainDashboardName && dashboardName === mainDashboardName) ||
    !mainDashboardName
  )
    yield put(layoutsActions.toggleSidebar(!dashboard.filters.length));

  const mappedDashboard = mapDashboard(
    dashboard,
    { dictionaries },
    disableFastMode
  );

  return mappedDashboard;
}

export async function apiSaveFilter(
  filter: ISaveFilterRequestModel
): Promise<ISaveFilterResponseModel> {
  let _filter = { ...filter };
  const filters = [..._filter.wrapper.filters];

  const isDemoFinded = filter.wrapper.filters.findIndex(
    ({ column }) => column === ISDEMO_FIELD_NAME
  );

  if (isDemoFinded >= 0) {
    filters.splice(isDemoFinded, 1);
    _filter = setIn(filter, filters, ['wrapper', 'filters']);
  }

  const result = await wretch('/api/dashboards/dashboard-filter')
    .post(_filter)
    .json<ISaveFilterResponseModel>();

  if (isDemoFinded) {
    mapIsDemoFilter(result.filters);
  }

  return result;
}

export async function apiUpdateFilter(filter: IUpdateFilterRequestModel) {
  let _filter = { ...filter };
  const filters = [..._filter.wrapper.filters];

  const isDemoFinded = filter.wrapper.filters.findIndex(
    ({ column }) => column === ISDEMO_FIELD_NAME
  );

  if (isDemoFinded >= 0) {
    filters.splice(isDemoFinded, 1);
    _filter = setIn(filter, filters, ['wrapper', 'filters']);
  }

  const result = await wretch(
    `/api/dashboards/dashboard-filter/${filter.dashboardId}`
  )
    .put(_filter)
    .json<IUpdateFilterResponseModel>();

  if (isDemoFinded) {
    mapIsDemoFilter(result.filters);
  }

  return result;
}

export async function apiDeleteFilter(
  requestModel: IDeleteFilterRequestModel
): Promise<IDeleteFilterResponseModel> {
  return wretch('/api/dashboards/dashboard-filter')
    .query({
      filterId: requestModel.filterId,
      dashboardId: requestModel.dashboardId,
    })
    .delete()
    .json();
}

export async function apiExcelExport(
  excelExportApi: string,
  requestModel: IExcelExportRequestModel,
  payload: any
): Promise<Response> {
  return wretch(
    `/api/${excelExportApi}?additionalColumns=${getExcelValue(payload)}`
  )
    .post(requestModel)
    .res();
}

interface IGridDataRequestModel extends IGridDataRequestOptions {
  gridApi: string;
  totalsApi?: string;
  dashboardName: string;
}

export function* apiGetGridData({
  gridApi,
  search,
  filters,
  sorting,
  currentPage,
  pageSize,
  totalsApi,
  settings,
  dashboardName,
}: IGridDataRequestModel) {
  const request = {
    filtering: {
      textFilter: search
        ? {
            searchFilter: search,
          }
        : undefined,
      filters,
    },
    paging: {
      sorting,
      from: (currentPage - 1) * pageSize,
      to: currentPage * pageSize,
    },
    isFastMode: settings.pageMode === PageMode.infinite,
  };
  const [result, totals] = yield Promise.all([
    wretch(gridApi)
      .post({ ...request, isFastMode: true })
      .json<IGridDataFromServer>(),
    totalsApi && settings.pageMode === 'pages'
      ? wretch(totalsApi).post(request).json<IGridTotalsResponseModel>()
      : Promise.resolve(null),
  ]);

  if (totals) {
    result.summary = totals.summary;
    result.count = totals.count;
  }

  yield put(
    dashboardActions.changeSettings(dashboardName, {
      key: 'isNextPageExists',
      value: result.isNextPageExists,
    })
  );

  return result;
}

export function* apiGetGridAllData(
  { gridApi, search, filters, sorting, settings }: IGridDataRequestModel,
  totalCount: number
) {
  const limit = 10000;

  if (totalCount > 0) {
    const requestCount = Math.ceil(totalCount / limit);
    const requests = Array(requestCount)
      .fill(null)
      .map((_, i) => {
        const request = {
          filtering: {
            textFilter: search
              ? {
                  searchFilter: search,
                }
              : undefined,
            filters,
          },
          paging: {
            sorting,
            from: i * limit,
            to: i * limit + limit,
          },
          isFastMode: settings.pageMode === PageMode.infinite,
        };

        return wretch(gridApi).post(request).json<IGridDataFromServer>();
      });

    const result = (yield Promise.all(requests)) as IGridDataFromServer[];

    return result.reduce((acc: any, item: any) => [...acc, ...item.rows], []);
  }

  if (settings.pageMode === PageMode.infinite) {
    let { isNextPageExists: canRepeatRequest } = settings;
    let i = 0;
    const resultRows: any[] = [];

    while (canRepeatRequest) {
      const request = {
        filtering: {
          textFilter: search
            ? {
                searchFilter: search,
              }
            : undefined,
          filters,
        },
        paging: {
          sorting,
          from: i * limit,
          to: i * limit + limit,
        },
        isFastMode: settings.pageMode === PageMode.infinite,
      };

      const result = (yield wretch(gridApi)
        .post(request)
        .json<IGridDataFromServer>()) as IGridDataFromServer;

      canRepeatRequest = result.isNextPageExists;
      i += 1;

      resultRows.push(...result.rows);
    }

    return resultRows;
  }

  return [];
}

export async function apiGetSavedFiltersByDashboardId(
  dashboardId: number
): Promise<ISavedFilterModel[]> {
  return wretch(`/api/dashboards/dashboard-filters?dashboardId=${dashboardId}`)
    .get()
    .json();
}

export const defaultApi: Pick<
  IDashboardApi,
  | 'apiSaveFilter'
  | 'apiUpdateFilter'
  | 'apiDeleteFilter'
  | 'apiGetSavedFiltersByDashboardId'
  | 'handleError'
> = {
  apiSaveFilter,
  apiUpdateFilter,
  apiDeleteFilter,
  apiGetSavedFiltersByDashboardId,
  handleError: (_sagaName: string, error: any) => handleServerError(error),
};
